import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import {
  Image, MessageCircle, Download, Loader2,
  Sparkles, Send, Copy, Trash2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PROMO_TYPES = [
  { id: 'instagram_post', label: 'Post Instagram', desc: '1080x1080', prompt_prefix: 'Square social media post (1080x1080) for Instagram.' },
  { id: 'instagram_story', label: 'Story Instagram', desc: '1080x1920', prompt_prefix: 'Vertical story format (1080x1920) for Instagram/TikTok.' },
  { id: 'banner_web', label: 'Banner Web', desc: '1920x600', prompt_prefix: 'Wide web banner (1920x600) for website header.' },
  { id: 'flyer', label: 'Flyer Digital', desc: '1080x1350', prompt_prefix: 'Digital flyer (1080x1350) for promotion.' },
  { id: 'youtube_thumb', label: 'Thumbnail YouTube', desc: '1280x720', prompt_prefix: 'YouTube thumbnail (1280x720) eye-catching.' },
];

const QUICK_PROMPTS = [
  'Oferta 50% en todos los beats esta semana',
  'Nuevo pack de Reggaeton disponible ahora',
  'Membresia OG Pro - acceso ilimitado a beats premium',
  'Coleccion Afro House exclusiva',
  'Beat del dia - Trap oscuro $5.99',
  'Lanzamiento: 20 tracks nuevos de produccion',
];

const MarketingStudio = () => {
  const [tab, setTab] = useState('create');
  // Banner creation
  const [promoType, setPromoType] = useState('instagram_post');
  const [promoText, setPromoText] = useState('');
  const [extraDetails, setExtraDetails] = useState('');
  const [generating, setGenerating] = useState(false);
  const [gallery, setGallery] = useState([]);
  // Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  const generatePromo = async () => {
    if (!promoText.trim()) return;
    const typeInfo = PROMO_TYPES.find(t => t.id === promoType) || PROMO_TYPES[0];
    setGenerating(true);
    try {
      const fullPrompt = `${typeInfo.prompt_prefix} 
Brand: OG MUSIC GROUP - Premium music store selling beats, instrumentals, packs and memberships.
Brand colors: Black background (#0a0a0c), Orange accent (#E8720C), white text.
Style: Cinematic, premium, urban, modern. Clean typography.
Content: ${promoText}
${extraDetails ? `Extra details: ${extraDetails}` : ''}
IMPORTANT: Include the brand name "OG MUSIC" prominently. Make text readable and professional. No blurry text.`;

      const r = await axios.post(`${API}/api/admin/marketing/generate-banner`, {
        prompt: fullPrompt,
      }, { withCredentials: true });

      if (r.data.image_url || r.data.image_base64) {
        setGallery(prev => [{
          id: Date.now(),
          url: r.data.image_url,
          image_base64: r.data.image_base64,
          type: typeInfo.label,
          text: promoText,
          created_at: new Date().toISOString(),
        }, ...prev]);
      }
    } catch (e) {
      alert('Error generando: ' + (e.response?.data?.detail || e.message));
    }
    setGenerating(false);
  };

  const downloadImage = (item) => {
    const a = document.createElement('a');
    if (item.image_base64) {
      a.href = `data:image/png;base64,${item.image_base64}`;
    } else {
      a.href = `${API}${item.url}`;
    }
    a.download = `OG_MUSIC_${item.type.replace(/\s/g, '_')}_${Date.now()}.png`;
    a.click();
  };

  // Chat
  const sendChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const msg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: msg }]);
    setChatLoading(true);
    try {
      const r = await axios.post(`${API}/api/admin/marketing/chat`, {
        message: msg,
        history: chatMessages.slice(-10),
      }, { withCredentials: true });
      setChatMessages(prev => [...prev, { role: 'assistant', content: r.data.response }]);
    } catch (e) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + (e.response?.data?.detail || e.message) }]);
    }
    setChatLoading(false);
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const tabs = [
    { id: 'create', label: 'Crear Promocion', icon: Image },
    { id: 'chat', label: 'Asistente Marketing', icon: MessageCircle },
  ];

  return (
    <div data-testid="marketing-studio">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Estudio de Marketing</h2>
        <p className="text-xs text-white/40 mt-1">Genera contenido promocional con IA para tus redes sociales</p>
      </div>

      <div className="flex gap-1 mb-6 bg-white/[0.03] p-1 rounded-xl" data-testid="marketing-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${
              tab === t.id ? 'bg-[#E8720C] text-white' : 'text-white/50 hover:text-white/80 hover:bg-white/5'
            }`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      {/* ═══ CREATE PROMO ═══ */}
      {tab === 'create' && (
        <div className="space-y-5">
          {/* Format selector */}
          <div className="glass-card p-5 border border-[#E8720C]/20">
            <h3 className="text-sm font-semibold mb-3">Formato</h3>
            <div className="flex gap-2 flex-wrap">
              {PROMO_TYPES.map(t => (
                <button key={t.id} onClick={() => setPromoType(t.id)}
                  className={`px-4 py-2.5 rounded-xl text-xs font-medium transition-all ${
                    promoType === t.id ? 'bg-[#E8720C] text-white ring-1 ring-[#E8720C]/50' : 'bg-white/5 text-white/50 hover:bg-white/10'
                  }`} data-testid={`format-${t.id}`}>
                  <span className="block font-bold">{t.label}</span>
                  <span className="block text-[9px] opacity-60 mt-0.5">{t.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Quick prompts */}
          <div>
            <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Ideas rapidas (haz clic para usar)</p>
            <div className="flex gap-2 flex-wrap">
              {QUICK_PROMPTS.map((q, i) => (
                <button key={i} onClick={() => setPromoText(q)}
                  className="px-3 py-1.5 bg-white/5 hover:bg-[#E8720C]/10 hover:text-[#E8720C] rounded-lg text-[10px] text-white/40 transition-colors">
                  {q}
                </button>
              ))}
            </div>
          </div>

          {/* Main prompt */}
          <div className="glass-card p-5">
            <label className="text-xs text-white/50 block mb-2">Que quieres promocionar?</label>
            <textarea value={promoText} onChange={e => setPromoText(e.target.value)}
              placeholder="Ej: Oferta especial - 50% de descuento en todos los beats de Reggaeton esta semana. Precios desde $2.99"
              className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/20 resize-none h-20 focus:outline-none focus:border-[#E8720C]/50 mb-3"
              data-testid="promo-text" />

            <label className="text-xs text-white/50 block mb-2">Detalles extra (opcional)</label>
            <input value={extraDetails} onChange={e => setExtraDetails(e.target.value)}
              placeholder="Ej: Incluir logo grande, poner precio $5.99, fondo con luces neon..."
              className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-xs text-white placeholder-white/20 focus:outline-none focus:border-[#E8720C]/50 mb-4"
              data-testid="promo-details" />

            <button onClick={generatePromo} disabled={generating || !promoText.trim()}
              className="px-6 py-3 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-50"
              data-testid="generate-promo-btn">
              {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generando imagen (~30s)...</> : <><Sparkles className="w-4 h-4" /> Generar Imagen Promocional</>}
            </button>
          </div>

          {/* Gallery */}
          {gallery.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-white/60 mb-3">Tus Imagenes ({gallery.length})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {gallery.map(item => (
                  <div key={item.id} className="glass-card overflow-hidden border border-white/10 group">
                    <div className="relative">
                      <img src={item.image_base64 ? `data:image/png;base64,${item.image_base64}` : `${API}${item.url}`}
                        alt="Promo" className="w-full object-contain bg-black" style={{ maxHeight: 400 }} />
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => downloadImage(item)}
                          className="p-2 bg-black/70 backdrop-blur-sm rounded-lg text-white hover:bg-[#E8720C]">
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 bg-[#E8720C]/10 text-[#E8720C] rounded text-[9px] font-bold">{item.type}</span>
                        <span className="text-[9px] text-white/20">{new Date(item.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-[10px] text-white/40 truncate">{item.text}</p>
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => downloadImage(item)}
                          className="px-3 py-1.5 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-lg text-[10px] font-bold flex items-center gap-1">
                          <Download className="w-3 h-3" /> Descargar
                        </button>
                        <button onClick={() => setPromoText(item.text)}
                          className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/50 rounded-lg text-[10px] flex items-center gap-1">
                          <Copy className="w-3 h-3" /> Reusar
                        </button>
                        <button onClick={() => setGallery(prev => prev.filter(g => g.id !== item.id))}
                          className="px-3 py-1.5 bg-white/5 hover:bg-red-500/10 text-white/30 hover:text-red-400 rounded-lg text-[10px] flex items-center gap-1">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ CHAT MARKETING ═══ */}
      {tab === 'chat' && (
        <div className="space-y-4">
          <div className="glass-card p-4 border border-blue-500/20">
            <p className="text-xs text-white/60">
              Preguntale a ChatGPT como crear contenido promocional, estrategias de marketing, ideas para videos, textos para redes sociales.
            </p>
          </div>

          <div className="glass-card p-4 min-h-[400px] max-h-[500px] overflow-y-auto flex flex-col" data-testid="marketing-chat">
            {chatMessages.length === 0 && (
              <div className="flex-1 flex items-center justify-center text-center">
                <div>
                  <MessageCircle className="w-10 h-10 text-white/10 mx-auto mb-3" />
                  <p className="text-white/30 text-sm mb-4">Preguntame sobre marketing...</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {[
                      'Dame 5 ideas para videos de TikTok',
                      'Escribe un caption para Instagram de un beat de Trap',
                      'Estrategia para vender mas packs',
                      'Como usar Runway Gen-3 para hacer videos',
                      'Prompt para generar un flyer de oferta',
                    ].map((q, i) => (
                      <button key={i} onClick={() => setChatInput(q)}
                        className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg text-[10px] text-white/40 hover:text-white/60 transition-colors">
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-3 flex-1">
              {chatMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-xs leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#E8720C] text-white rounded-br-sm'
                      : 'bg-white/[0.05] text-white/80 rounded-bl-sm border border-white/5'
                  }`}>
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="px-4 py-2.5 bg-white/[0.05] rounded-2xl rounded-bl-sm border border-white/5">
                    <Loader2 className="w-4 h-4 animate-spin text-[#E8720C]" />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          </div>

          <div className="flex gap-2">
            <input value={chatInput} onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChat()}
              placeholder="Escribe tu pregunta..."
              className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#E8720C]/50"
              data-testid="marketing-chat-input" />
            <button onClick={sendChat} disabled={chatLoading || !chatInput.trim()}
              className="px-5 py-3 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-50"
              data-testid="marketing-chat-send">
              {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingStudio;
